import type {
  BoardData,
  BoardItem,
  CollectionData,
  CollectionGroup,
  CollectionTreeNode,
  Person,
  PollData,
  ProjectDoc,
  ProjectMeta,
  RecordData,
} from '@tomo/core';
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useStore } from '../lib/store.ts';
import { getRegistryEntry } from './registry.ts';
import { acquireProject, markOpened, releaseProject, type PresenceUser, type ProjectSession, type SyncState } from './projects.ts';

const SessionContext = createContext<ProjectSession | null>(null);

export type ProjectLoadState = { status: 'loading' } | { status: 'ready'; session: ProjectSession } | { status: 'missing' } | { status: 'waiting-cloud'; session: ProjectSession };

/** Open a project for the lifetime of the calling component. */
export function useProjectLoader(projectId: string): ProjectLoadState {
  const [state, setState] = useState<ProjectLoadState>({ status: 'loading' });
  useEffect(() => {
    const session = acquireProject(projectId);
    let alive = true;
    let unsub: (() => void) | undefined;
    setState({ status: 'loading' });
    session.ready.then(() => {
      if (!alive) return;
      if (session.project.isInitialized()) {
        setState({ status: 'ready', session });
        void markOpened(projectId);
        return;
      }
      if (getRegistryEntry(projectId)?.cloud) {
        // A cloud project opened on a new device: wait for the first sync to bring the content.
        setState({ status: 'waiting-cloud', session });
        unsub = session.project.subscribe(() => {
          if (session.project.isInitialized()) {
            unsub?.();
            setState({ status: 'ready', session });
            void markOpened(projectId);
          }
        });
        return;
      }
      setState({ status: 'missing' });
    });
    return () => {
      alive = false;
      unsub?.();
      releaseProject(session);
    };
  }, [projectId]);
  return state;
}

export function SessionProvider({ session, children }: { session: ProjectSession; children: ReactNode }) {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): ProjectSession {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession must be used inside a project');
  return s;
}

export function useOptionalSession(): ProjectSession | null {
  return useContext(SessionContext);
}

export function useProject(): ProjectDoc {
  return useSession().project;
}

export function useProjectId(): string {
  return useSession().id;
}

/**
 * Subscribe to a slice of the project. `select` must return cached values (all ProjectDoc getters
 * do) — derive new objects with `useMemo` on top instead.
 */
export function useProjectValue<T>(select: (p: ProjectDoc) => T): T {
  const p = useProject();
  return useSyncExternalStore(p.subscribe, () => select(p));
}

export const useProjectVersion = () => useProjectValue((p) => p.version);
export const useMeta = (): ProjectMeta => useProjectValue((p) => p.getMeta());
export const useCollections = (): CollectionData[] => useProjectValue((p) => p.listCollections());
export const useCollection = (id: string | null | undefined): CollectionData | undefined => useProjectValue((p) => p.getCollection(id));
export const useCollectionGroups = (): CollectionGroup[] => useProjectValue((p) => p.listCollectionGroups());
export const useCollectionGroup = (id: string | null | undefined): CollectionGroup | undefined => useProjectValue((p) => p.getCollectionGroup(id));
export const useCollectionTree = (): CollectionTreeNode[] => useProjectValue((p) => p.collectionTree());
export const useRecords = (collectionId: string): RecordData[] => useProjectValue((p) => p.listRecords(collectionId));
export const useRecord = (id: string | null | undefined): RecordData | undefined => useProjectValue((p) => p.getRecord(id));
export const useAllRecords = (): RecordData[] => useProjectValue((p) => p.allRecords());
export const usePolls = (): PollData[] => useProjectValue((p) => p.listPolls());
export const usePoll = (id: string | null | undefined): PollData | undefined => useProjectValue((p) => p.getPoll(id));
export const useBoards = (): BoardData[] => useProjectValue((p) => p.listBoards());
export const useBoard = (id: string | null | undefined): BoardData | undefined => useProjectValue((p) => p.getBoard(id));
export const useBoardItems = (id: string): BoardItem[] => useProjectValue((p) => p.listBoardItems(id));
export const usePeople = (): Person[] => useProjectValue((p) => p.listPeople());
export const useBacklinks = (id: string): readonly string[] => useProjectValue((p) => p.backlinks(id));

export function usePersonMap(): Map<string, Person> {
  const people = usePeople();
  return useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
}

/** Map of record id → record for every live record (relation pickers, mentions). */
export function useRecordMap(): Map<string, RecordData> {
  const all = useAllRecords();
  return useMemo(() => new Map(all.map((r) => [r.id, r])), [all]);
}

export function useSyncState(): SyncState {
  return useStore(useSession().sync);
}

/** Viewers of a cloud project: hide editing affordances (the document refuses writes anyway). */
export const useReadOnly = (): boolean => useProjectValue((p) => p.readOnly);

export function usePresence(): PresenceUser[] {
  return useStore(useSession().presence);
}

/** Undo/redo availability for the structured (non-editor) undo stack. */
export function useUndoState(): { canUndo: boolean; canRedo: boolean } {
  const p = useProject();
  const [, force] = useState(0);
  useEffect(() => {
    const um = p.undoManager;
    const bump = () => force((n) => n + 1);
    um.on('stack-item-added', bump);
    um.on('stack-item-popped', bump);
    um.on('stack-cleared', bump);
    return () => {
      um.off('stack-item-added', bump);
      um.off('stack-item-popped', bump);
      um.off('stack-cleared', bump);
    };
  }, [p]);
  return { canUndo: p.undoManager.canUndo(), canRedo: p.undoManager.canRedo() };
}
